// src/features/userSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInstance';
import { logout } from './authSlice';
import { 
    cacheUserData, 
    getCachedUserData, 
    isUserCacheFresh, 
    clearUserCache 
} from '../utils/userCache';

/**
 * Async thunk for fetching user data with built-in request deduplication
 * and sessionStorage caching for instant display across page navigations.
 * 
 * Caching strategy (similar to Auth0/Firebase patterns):
 * 1. Check if data exists in Redux state (already loaded this session)
 * 2. Check if fresh cached data exists in sessionStorage
 * 3. Only make API call if no valid cache exists
 * 4. Cache is invalidated after 1 hour or on logout
 */
export const fetchUserData = createAsyncThunk(
  'user/fetchUserData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/users/');
      const userData = response.data[0];
      
      // Cache the fresh data to sessionStorage
      cacheUserData(userData);
      
      return userData;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    // Prevent duplicate requests - this runs BEFORE the thunk executes
    condition: (_, { getState }) => {
      const { user } = getState();
      // Only fetch if status is 'idle' (never fetched)
      // Skip if already loading or succeeded
      if (user.status === 'loading' || user.status === 'succeeded') {
        return false; // Cancel the thunk - don't execute
      }
      return true; // Proceed with the thunk
    },
  }
);

/**
 * Initialize user state from sessionStorage cache.
 * Called once when the app loads to provide instant user data
 * before any API calls complete.
 */
const getInitialState = () => {
  const cachedData = getCachedUserData();
  const isFresh = isUserCacheFresh();
  
  if (cachedData && isFresh) {
    // We have fresh cached data - use it immediately
    // The status is set to 'succeeded' so we don't trigger a fetch
    return {
      userData: cachedData,
      status: 'succeeded',
      error: null,
    };
  }
  
  // No cache or stale cache - start fresh
  return {
    userData: null,
    status: 'idle',
    error: null,
  };
};

const initialState = getInitialState();

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserData: (state) => {
      state.userData = null;
      state.status = 'idle';
      state.error = null;
      clearUserCache();
    },
    /**
     * Force a refresh of user data by resetting status to 'idle'.
     * Use this when you need to refetch data regardless of cache.
     */
    invalidateUserCache: (state) => {
      state.status = 'idle';
      clearUserCache();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.userData = action.payload;
        state.error = null;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Reset state on logout and clear cache
      .addCase(logout, (state) => {
        state.userData = null;
        state.status = 'idle';
        state.error = null;
        clearUserCache();
      });
  },
});

export const { clearUserData, invalidateUserCache } = userSlice.actions;

// Selectors
export const selectUserData = (state) => state.user.userData;
export const selectUserStatus = (state) => state.user.status;
export const selectUserError = (state) => state.user.error;

export default userSlice.reducer;
